import { ChevronRight } from 'lucide-react';
import type { WebGoal } from '../../../lib/goals';
import { goalMembers, membersDone } from '../../../lib/goals';
import { goalArc, goalBadge } from '../../../lib/goal-status';
import { useTranslation } from '../../../lib/i18n';
import { Badge, Card, ProgressRing, Typography } from '../ui';
import styles from './GoalCard.module.css';

// One goal card for the Goals-tab grid (Figma 10666:89977 / 10666:90268): an 80px completion ring beside
// the status badge over the goal name, and a trailing chevron (as in the Figma and native's
// `ChevronRightIcon`). Clicking anywhere on it opens the goal's plan in the shared side panel. Mirrors the
// native `GoalRow` — same 80/12 ring, same badge-over-title stack, and the same rule that a 100%-complete
// plan reads "Done" whatever the stored entity status says.
//
// A goal made of MEMBERS reads its members here too: the ring already counts them (the route rolls members
// into `percent` alongside the goal's own tasks), and a "{done} of {total} members done" line under the
// title says what the ring is counting — otherwise a container goal's progress would have no visible
// source on the card. The badge defers to a recorded OUTCOME when there is one, so a goal that was called
// off never wears the green Done chip.
export function GoalCard({ goal, onOpen }: { goal: WebGoal; onOpen: () => void }) {
  const { t } = useTranslation();
  const badge = goalBadge(goal);
  const members = goalMembers(goal);
  return (
    <Card component="button" variant="shadow" interactive className={styles.card} onClick={onOpen}>
      <ProgressRing percent={goal.percent} arcColor={goalArc(goal)} size={64} stroke={10} />
      <span className={styles.body}>
        <Badge variant={badge.variant} label={t(badge.labelKey)} />
        <Typography variant="p1" color="strong" component="span" className={styles.title}>
          {goal.title || t('goals.untitled')}
        </Typography>
        {members.length > 0 && (
          <Typography variant="p3" color="neutral" component="span" className={styles.members}>
            {t('goals.members.count', { done: membersDone(members), total: members.length })}
          </Typography>
        )}
      </span>
      <ChevronRight aria-hidden size={20} className={`${styles.chevron} dir-flip`} />
    </Card>
  );
}
